import { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Cell
} from 'recharts';
import DatePicker from '../../components/DatePicker';
import YilDropdown from '../../components/YilDropdown';
import UsageInfo from '../../components/UsageInfo';
import { getForecast } from '../../services/api';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';

// ── Yardımcılar ───────────────────────────────────────────────────────────
function pad(n) { return String(n).padStart(2, '0'); }
function dateToStr(d) { return pad(d.getDate()) + '.' + pad(d.getMonth()+1) + '.' + d.getFullYear(); }
function strToIso(s) { if (!s) return ''; const p = s.split('.'); return p[2]+'-'+p[1]+'-'+p[0]; }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

const AYLAR     = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
const AYLAR_KSA = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];

function ayBaslangic(ay, yil) { return dateToStr(new Date(yil, ay, 1)); }
function ayBitis(ay, yil)     { return dateToStr(new Date(yil, ay + 1, 0)); }

// ── Özet Kart ─────────────────────────────────────────────────────────────
function KartOzet({ label, value, sub, bgClass, textClass, icon }) {
  return (
    <div className={`relative overflow-hidden rounded-[2rem] p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group ${bgClass} border border-white/40 shadow-lg transform-gpu`}>
      <div className="absolute -right-4 -bottom-4 text-7xl opacity-5 group-hover:scale-125 group-hover:-rotate-12 transition-transform duration-500">{icon}</div>
      <div className="flex flex-col h-full justify-between relative z-10">
        <div className="flex items-start justify-between mb-2">
          <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${textClass}`}>{label}</span>
          <span className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl bg-white/80 backdrop-blur-md shadow-sm font-emoji">{icon}</span>
        </div>
        <div className="mt-2 text-3xl font-black text-gray-900 tracking-tighter leading-none">{value}</div>
        {sub && <p className={`text-[9px] font-black mt-3 tracking-widest uppercase opacity-80 ${textClass}`}>{sub}</p>}
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/90 backdrop-blur-xl border border-white/40 rounded-2xl shadow-2xl px-5 py-4 text-xs transform-gpu">
      <p className="font-black text-gray-900 mb-3 tracking-tight">{label}</p>
      <div className="space-y-2">
        {payload.map(p => (
          <div key={p.name} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full shadow-sm" style={{ background: p.fill || p.color }} />
              <span className="text-gray-500 font-bold uppercase tracking-widest text-[9px]">{p.name}</span>
            </div>
            <span className="font-black text-gray-900">{p.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Ana Bileşen ───────────────────────────────────────────────────────────
export default function Forecast() {
  const bugunD   = new Date();
  const bugunAy  = bugunD.getMonth();
  const bugunYil = bugunD.getFullYear();

  const [yil,        setYil]        = useState(bugunYil);
  const [selectedAy, setSelectedAy] = useState(bugunAy);
  const [basTarih,   setBasTarih]   = useState(ayBaslangic(bugunAy, bugunYil));
  const [bitTarih,   setBitTarih]   = useState(ayBitis(bugunAy, bugunYil));
  const [aktifBas,   setAktifBas]   = useState(basTarih);
  const [aktifBit,   setAktifBit]   = useState(bitTarih);
  const [grafikTip,  setGrafikTip]  = useState('stacked');

  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const fetchData = useCallback(async (bas, bit) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getForecast(strToIso(bas), strToIso(bit));
      setData(Array.isArray(res) ? res : []);
    } catch {
      setError('Veriler yüklenemedi.');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(aktifBas, aktifBit); }, [aktifBas, aktifBit, fetchData]);

  function handleAyChange(ay) {
    setSelectedAy(ay);
    setBasTarih(ayBaslangic(ay, yil));
    setBitTarih(ayBitis(ay, yil));
  }

  function handleYilChange(y) {
    setYil(y);
    setBasTarih(ayBaslangic(selectedAy, y));
    setBitTarih(ayBitis(selectedAy, y));
  }
  function handleBasChange(val) {
    setBasTarih(val);
    const db = new Date(strToIso(val)), de = new Date(strToIso(bitTarih));
    if (db > de) setBitTarih(val);
  }
  function handleBitChange(val) {
    const db = new Date(strToIso(basTarih)), de = new Date(strToIso(val));
    setBitTarih(de >= db ? val : basTarih);
  }
  function filtrele() { setAktifBas(basTarih); setAktifBit(bitTarih); }
  function temizle() {
    const nb = ayBaslangic(bugunAy, bugunYil), ne = ayBitis(bugunAy, bugunYil);
    setYil(bugunYil); setSelectedAy(bugunAy);
    setBasTarih(nb); setBitTarih(ne); setAktifBas(nb); setAktifBit(ne);
  }

  // Özet hesaplar
  const toplamDolu   = data.reduce((s, r) => s + r.doluOda, 0);
  const toplamBos    = data.reduce((s, r) => s + r.bosOda, 0);
  const toplamBloke  = data.reduce((s, r) => s + r.blokeOda, 0);
  const toplamYet    = data.reduce((s, r) => s + r.yetiskin, 0);
  const toplamCoc    = data.reduce((s, r) => s + r.cocuk, 0);
  const toplamBeb    = data.reduce((s, r) => s + r.bebek, 0);
  const toplamGunluk = data.reduce((s, r) => s + r.gunlukToplamMisafir, 0);
  const toplamGiris  = data.reduce((s, r) => s + r.girisSayisi, 0);
  const ortDoluluk   = data.length ? Math.round(data.reduce((s, r) => s + r.doluluk, 0) / data.length) : 0;

  // Grafik veri — uzun aralıklarda seyrelt
  const grafikData = data.length > 60 ? data.filter((_, i) => i % 3 === 0) : data;

  function excelExport() {
    const baslik = ['Gün/Tarih','Oda','Dolu','Boş','Bloke','Yet.(+12)','Çoc.(7-12)','Beb.(0-6)','GÜNLÜK','Giriş Sayısı','Doluluk %'];
    const satirlar = data.map(r =>
      [r.tarih, r.toplamOda, r.doluOda, r.bosOda, r.blokeOda, r.yetiskin, r.cocuk, r.bebek, r.gunlukToplamMisafir, r.girisSayisi, r.doluluk + '%']
    );
    downloadExcel(`forecast-${aktifBas}-${aktifBit}`, 'Forecast', baslik, satirlar);
  }

  function yazdir() {
    const baslik = ['Tarih','Oda','Dolu','Boş','Yet.','Çoc.','Beb.','Günlük','Doluluk %'];
    const satirlar = data.map(r => [r.tarih, r.toplamOda, r.doluOda, r.bosOda, r.yetiskin, r.cocuk, r.bebek, r.gunlukToplamMisafir, r.doluluk + '%']);
    printTable('Forecast Raporu', baslik, satirlar, aktifBas + ' – ' + aktifBit + ' · ' + data.length + ' gün');
  }

  return (
    <div className="p-3 md:p-6 space-y-8 min-h-screen bg-gray-50/50 transform-gpu" style={{ animation: 'fadeIn 0.3s ease-out' }}>

      {/* Premium Header */}
      <div className="relative overflow-hidden bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-xl p-4 md:p-8 transform-gpu">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-blue-100 rounded-full blur-[80px] opacity-50 will-change-transform"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-indigo-100 rounded-full blur-[80px] opacity-40 will-change-transform"></div>
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="flex items-center gap-6 min-w-0">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-700 rounded-2xl flex-shrink-0 flex items-center justify-center text-white text-3xl shadow-2xl shadow-blue-200 transform-gpu hover:scale-105 transition-transform duration-500">📊</div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tighter truncate">Gelişmiş Öngörü Raporu</h1>
              <p className="text-sm font-bold text-gray-400 mt-1 uppercase tracking-widest truncate">Geleceğe Dair Doluluk, Talep & Tahmin Analizi</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            <button onClick={excelExport} className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-white/80 backdrop-blur-md border-2 border-emerald-100/50 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 font-black px-6 py-4 rounded-2xl shadow-lg shadow-emerald-50 transition-all text-[10px] uppercase tracking-widest hover:-translate-y-1 active:scale-95 whitespace-nowrap transform-gpu">
              <span>📊</span> EXCEL'E AKTAR
            </button>
            <button onClick={yazdir} className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-white/80 backdrop-blur-md border-2 border-gray-100/50 hover:border-gray-800 hover:bg-gray-800 hover:text-white text-gray-700 font-black px-6 py-4 rounded-2xl shadow-lg shadow-gray-50 transition-all text-[10px] uppercase tracking-widest hover:-translate-y-1 active:scale-95 whitespace-nowrap transform-gpu">
              <span>🖨️</span> YAZDIR
            </button>
          </div>
        </div>
      </div>

      {/* Hata */}
      {error && (
        <div className="bg-red-50/80 backdrop-blur-md border border-red-200/50 text-red-700 rounded-2xl px-5 py-3 text-sm font-bold shadow-lg shadow-red-50 transform-gpu animate-fade-in">{error}</div>
      )}

      {/* Akıllı Filtre Paneli */}
      <div className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl p-6 md:p-8 z-20 relative transform-gpu">
        <div className="flex flex-col lg:flex-row gap-6 lg:items-end">
          <div className="flex-[2] flex items-center gap-4 flex-wrap pb-4 lg:pb-0 lg:border-r border-gray-100/50 lg:pr-8">
            <div className="w-full">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">Ay / Yıl Üzerinden Hızlı Seçim</label>
              <div className="flex items-center gap-3 w-full sm:w-auto h-full">
                <YilDropdown value={yil} onChange={handleYilChange} />
                <select value={selectedAy} onChange={e => handleAyChange(Number(e.target.value))} className="w-full sm:w-48 bg-gray-50/50 border-2 border-transparent focus:bg-white focus:border-blue-500 rounded-2xl px-5 py-4 font-black text-sm text-gray-700 outline-none appearance-none cursor-pointer transition-all">
                  {AYLAR.map((a, i) => <option key={a} value={i}>{a}</option>)}
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex-[3] flex flex-col sm:flex-row gap-4 sm:gap-6 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-2.5 ml-1">Özel Başlangıç</label>
              <DatePicker value={basTarih} onChange={handleBasChange} />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-2.5 ml-1">Özel Bitiş</label>
              <DatePicker value={bitTarih} onChange={handleBitChange} minDate={basTarih} />
            </div>
          </div>

          <div className="flex-[1] flex flex-col sm:flex-row lg:flex-col gap-3 min-w-[140px]">
            <button onClick={filtrele} disabled={loading} className="w-full flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white pt-5 pb-5 px-6 rounded-[1.5rem] font-black text-[10px] uppercase tracking-widest shadow-2xl shadow-blue-200 transition-all active:scale-95 flex items-center justify-center leading-none transform-gpu">
              {loading ? 'YÜKLENİYOR...' : '🔍 FİLTRELE'}
            </button>
            <button onClick={temizle} className="w-full sm:w-auto lg:w-full bg-gray-50 hover:bg-gray-100 text-gray-400 border-2 border-gray-100 py-3.5 px-6 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all hover:text-gray-600 flex justify-center items-center transform-gpu">
              Temizle
            </button>
          </div>
        </div>
      </div>

      {/* Özet Kartlar */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 xl:grid-cols-7 xl:gap-5 transform-gpu">
        <KartOzet label="Dolu Oda"    value={loading ? '…' : toplamDolu}   sub={`${ortDoluluk}% Ort. Doluluk`} bgClass="bg-gradient-to-br from-blue-50/50 to-indigo-50/50" textClass="text-blue-600"   icon="🛏️" />
        <KartOzet label="Boş Oda"     value={loading ? '…' : toplamBos}    sub={`${data.length} Gün Toplamı`}   bgClass="bg-gradient-to-br from-emerald-50/50 to-green-50/50" textClass="text-emerald-600"   icon="🟢" />
        <KartOzet label="Bloke Oda"   value={loading ? '…' : toplamBloke}  sub="Bakım & Arızalı"                bgClass="bg-gradient-to-br from-orange-50/50 to-amber-50/50" textClass="text-orange-600"  icon="🔒" />
        <KartOzet label="Yetişkin"    value={loading ? '…' : toplamYet}    sub="+12 Yaş Tümü"                   bgClass="bg-gradient-to-br from-purple-50/50 to-fuchsia-50/50" textClass="text-purple-600"  icon="🧑" />
        <KartOzet label="Çocuk"       value={loading ? '…' : toplamCoc}    sub="7-12 Yaş Aralığı"               bgClass="bg-gradient-to-br from-pink-50/50 to-rose-50/50" textClass="text-pink-600"      icon="🧒" />
        <KartOzet label="Bebek"       value={loading ? '…' : toplamBeb}    sub="0-6 Yaş Aralığı"                bgClass="bg-gradient-to-br from-yellow-50/50 to-amber-50/50" textClass="text-amber-600"  icon="👶" />
        <KartOzet label="Sirkülasyon" value={loading ? '…' : toplamGiris}  sub="Yeni Check-in Sayısı"           bgClass="bg-gradient-to-br from-teal-50/50 to-cyan-50/50" textClass="text-teal-600"  icon="🚀" />
      </div>

      {/* Grafik */}
      {!loading && data.length > 0 && (
        <div className="bg-white/90 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl p-6 md:p-10 overflow-hidden transform-gpu">
          <style>{`
            @keyframes chartSlideUp { from { opacity:0; transform:translateY(40px); } to { opacity:1; transform:translateY(0); } }
            .chart-appear { animation: chartSlideUp 0.8s cubic-bezier(.22,1,.36,1) both; will-change: transform, opacity; }
          `}</style>

          <div className="flex items-center justify-between xl:items-end mb-8 flex-col xl:flex-row gap-6">
            <div className="w-full xl:w-auto">
              <h2 className="text-xl font-black text-gray-800 tracking-tighter uppercase mb-1 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500 shadow-sm shadow-indigo-100 transform-gpu hover:rotate-12 transition-transform duration-500">📈</span> Görselleştirme Trendi
              </h2>
              <p className="text-[10px] font-black uppercase text-gray-400 tracking-[0.2em]">{aktifBas} — {aktifBit}</p>
            </div>
            <div className="flex flex-wrap gap-2 bg-gray-50/50 backdrop-blur-md rounded-[2rem] p-2 border border-white/40 self-start xl:self-auto w-full xl:w-auto transform-gpu shadow-inner">
              {[
                { key:'stacked', label:'🏨 Oda Dağılımı' },
                { key:'doluluk', label:'📊 Doluluk Yüzdesi' },
                { key:'misafir', label:'👥 Misafir Akışı' },
              ].map(t => (
                <button key={t.key} onClick={() => setGrafikTip(t.key)}
                  className={`flex-1 xl:flex-none px-6 py-4 xl:py-3 text-[10px] font-black uppercase tracking-widest rounded-2xl transition-all duration-300 transform-gpu ${grafikTip === t.key ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-200 scale-[1.02]' : 'text-gray-500 hover:text-gray-800 hover:bg-white'} border border-transparent`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mb-4">
            {grafikTip === 'stacked' && [
              { color:'#3b82f6', label:'Dolu Oda', val: toplamDolu },
              { color:'#f59e0b', label:'Bloke',    val: toplamBloke },
              { color:'#10b981', label:'Boş Oda',  val: toplamBos },
            ].map(l => (
              <div key={l.label} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/50 rounded-xl border border-white/40 shadow-sm transform-gpu hover:-translate-y-0.5 transition-transform">
                <span className="w-3 h-3 rounded-sm inline-block shadow-sm" style={{ background: l.color }} />
                <span className="text-[10px] text-gray-500 font-black uppercase tracking-tight">{l.label}</span>
                <span className="text-[11px] font-black px-2 py-0.5 rounded-lg" style={{ background: l.color+'22', color: l.color }}>{l.val}</span>
              </div>
            ))}
            {grafikTip === 'doluluk' && (
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-50/50 rounded-xl border border-white/40 shadow-sm">
                  <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500 shadow-sm" /><span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">&lt;50%</span></span>
                  <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-400 shadow-sm" /><span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">50–80%</span></span>
                  <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 shadow-sm" /><span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">&gt;80%</span></span>
                </div>
                <div className="ml-auto px-4 py-1.5 bg-blue-50/50 rounded-xl border border-blue-100 shadow-sm">
                   <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest mr-2">Ort. Doluluk:</span>
                   <span className="text-sm font-black text-blue-600">{ortDoluluk}%</span>
                </div>
              </div>
            )}
            {grafikTip === 'misafir' && [
              { color:'#8b5cf6', label:'Yetişkin', val: toplamYet },
              { color:'#ec4899', label:'Çocuk',    val: toplamCoc },
              { color:'#f59e0b', label:'Bebek',    val: toplamBeb },
              { color:'#3b82f6', label:'Toplam',   val: toplamGunluk },
            ].map(l => (
              <div key={l.label} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/50 rounded-xl border border-white/40 shadow-sm transform-gpu hover:-translate-y-0.5 transition-transform">
                <span className="w-2.5 h-2.5 rounded-full shadow-sm" style={{ background: l.color }} />
                <span className="text-[10px] text-gray-500 font-black uppercase tracking-tight">{l.label}</span>
                <span className="text-[11px] font-black px-2 py-0.5 rounded-lg" style={{ background: l.color+'22', color: l.color }}>{l.val}</span>
              </div>
            ))}
          </div>

          <div key={grafikTip + aktifBas + aktifBit} className="chart-appear will-change-transform">
            <ResponsiveContainer width="100%" height={300}>
              {grafikTip === 'stacked' ? (
                <BarChart data={grafikData} margin={{ top:10, right:10, left:-15, bottom:5 }}
                  barSize={data.length > 20 ? 12 : 24} barCategoryGap="20%">
                  <defs>
                    <linearGradient id="gradDolu"  x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#60a5fa"/><stop offset="100%" stopColor="#2563eb"/></linearGradient>
                    <linearGradient id="gradBos"   x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#34d399"/><stop offset="100%" stopColor="#059669"/></linearGradient>
                    <linearGradient id="gradBloke" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#fcd34d"/><stop offset="100%" stopColor="#d97706"/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="gunLabel" tick={{ fontSize:10, fill:'#94a3b8', fontWeight:700 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize:10, fill:'#94a3b8', fontWeight:700 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(99,102,241,0.06)', radius:8 }} />
                  <Bar dataKey="doluOda"  name="Dolu Oda" stackId="a" fill="url(#gradDolu)"  radius={[0,0,0,0]} isAnimationActive animationDuration={800} />
                  <Bar dataKey="blokeOda" name="Bloke"    stackId="a" fill="url(#gradBloke)" radius={[0,0,0,0]} isAnimationActive animationDuration={900} />
                  <Bar dataKey="bosOda"   name="Boş Oda"  stackId="a" fill="url(#gradBos)"   radius={[8,8,0,0]} isAnimationActive animationDuration={1000} />
                </BarChart>
              ) : grafikTip === 'doluluk' ? (
                <BarChart data={grafikData} margin={{ top:10, right:10, left:-15, bottom:5 }} barSize={data.length > 20 ? 12 : 24}>
                  <defs>
                    {grafikData.map((entry, index) => {
                      const c1 = entry.doluluk >= 80 ? '#fca5a5' : entry.doluluk >= 50 ? '#fde68a' : '#6ee7b7';
                      const c2 = entry.doluluk >= 80 ? '#dc2626' : entry.doluluk >= 50 ? '#d97706' : '#059669';
                      return (
                        <linearGradient key={index} id={`barGrad${index}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="gunLabel" tick={{ fontSize:10, fill:'#94a3b8', fontWeight:700 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0,100]} unit="%" tick={{ fontSize:10, fill:'#94a3b8', fontWeight:700 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(99,102,241,0.06)', radius:8 }} />
                  <Bar dataKey="doluluk" name="Doluluk %" radius={[8,8,0,0]} isAnimationActive animationDuration={800}>
                    {grafikData.map((_, index) => <Cell key={index} fill={`url(#barGrad${index})`} />)}
                  </Bar>
                </BarChart>
              ) : (
                <LineChart data={grafikData} margin={{ top:10, right:10, left:-15, bottom:5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="gunLabel" tick={{ fontSize:10, fill:'#94a3b8', fontWeight:700 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize:10, fill:'#94a3b8', fontWeight:700 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ stroke:'#e5e7eb', strokeWidth:2 }} />
                  <Line type="monotone" dataKey="yetiskin"            name="Yetişkin"      stroke="#8b5cf6" strokeWidth={3} dot={false} isAnimationActive animationDuration={1000} />
                  <Line type="monotone" dataKey="cocuk"               name="Çocuk"         stroke="#ec4899" strokeWidth={2.5} dot={false} isAnimationActive animationDuration={1100} strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="bebek"               name="Bebek"         stroke="#f59e0b" strokeWidth={2}   dot={false} isAnimationActive animationDuration={1200} strokeDasharray="3 3" />
                  <Line type="monotone" dataKey="gunlukToplamMisafir" name="Günlük Toplam" stroke="#3b82f6" strokeWidth={4}   dot={false} isAnimationActive animationDuration={900} />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tablo – Mobil Kartlar */}
      <div className="md:hidden space-y-3 transform-gpu">
        {loading ? (
          <LoadingSpinner fullPage text="Veriler Hazırlanıyor" color="blue" />
        ) : data.length === 0 ? (
          <EmptyState icon="📭" text="Seçilen tarih aralığında veri bulunamadı" />
        ) : data.map((r, idx) => {
          const isToday = r.tarih === dateToStr(new Date());
          return (
            <div key={r.tarih} className={`rounded-[2rem] border-2 p-5 transition-all transform-gpu hover:shadow-2xl hover:-translate-y-1 ${isToday ? 'bg-blue-50/80 border-blue-200 shadow-xl' : idx%2===0 ? 'bg-white/80 border-white/40 shadow-xl' : 'bg-gray-50/50 border-gray-100 shadow-lg'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {isToday ? <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse shadow-lg shadow-blue-200" /> : <span className="w-2 h-2 rounded-full bg-gray-200" />}
                  <span className="text-base font-black text-gray-900 tracking-tight">{r.tarih}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/50 px-3 py-1.5 rounded-xl border border-white shadow-sm">
                  <div className="w-12 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full shadow-sm transition-all duration-700 ${r.doluluk>=80?'bg-red-500':r.doluluk>=50?'bg-yellow-400':'bg-green-500'}`} style={{ width:r.doluluk+'%' }} />
                  </div>
                  <span className={`text-xs font-black ${r.doluluk>=80?'text-red-600':r.doluluk>=50?'text-yellow-600':'text-green-600'}`}>{r.doluluk}%</span>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 text-center pb-4 border-b border-gray-100/50">
                <div><p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Dolu</p><p className="font-black text-blue-600 text-lg">{r.doluOda}</p></div>
                <div><p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Boş</p><p className={`font-black text-lg ${r.bosOda<5?'text-red-500':'text-green-600'}`}>{r.bosOda}</p></div>
                <div><p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Bloke</p><p className={`font-black text-lg ${r.blokeOda>0?'text-orange-500':'text-gray-400'}`}>{r.blokeOda}</p></div>
                <div><p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Giriş</p><p className="font-black text-gray-900 text-lg">{r.girisSayisi}</p></div>
              </div>
              <div className="flex justify-between items-center pt-4">
                 <div className="flex gap-4">
                    <div className="text-center"><p className="text-[8px] font-black text-gray-300 uppercase tracking-widest mb-0.5">Yetişkin</p><p className="font-black text-purple-600 text-sm">{r.yetiskin}</p></div>
                    <div className="text-center"><p className="text-[8px] font-black text-gray-300 uppercase tracking-widest mb-0.5">Çocuk</p><p className="font-black text-pink-600 text-sm">{r.cocuk}</p></div>
                    <div className="text-center"><p className="text-[8px] font-black text-gray-300 uppercase tracking-widest mb-0.5">Bebek</p><p className="font-black text-amber-500 text-sm">{r.bebek}</p></div>
                 </div>
                 <div className="text-right bg-indigo-50/50 px-4 py-2 rounded-2xl border border-indigo-100 shadow-sm">
                    <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest mb-0.5">Günlük Toplam</p>
                    <p className="font-black text-indigo-700 text-base leading-none">{r.gunlukToplamMisafir}</p>
                 </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tablo – Masaüstü */}
      <div className="hidden md:block bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl overflow-hidden transform-gpu">
        <div className="flex items-center justify-between px-10 py-6 border-b border-gray-100/50 bg-gray-50/30">
          <p className="text-sm font-bold text-gray-500">
            Toplam <span className="text-blue-600 font-extrabold">{data.length}</span> kayıtlık projeksiyon sunulmaktadır.
          </p>
          <div className="flex items-center gap-3">
             <span className="flex items-center gap-1.5 text-[9px] font-black text-gray-400 uppercase tracking-widest"><span className="w-2 h-2 rounded-full bg-blue-500 shadow-sm" />Bugün</span>
             <span className="flex items-center gap-1.5 text-[9px] font-black text-gray-400 uppercase tracking-widest"><span className="w-2 h-2 rounded-full bg-red-500 shadow-sm" />Kritik Boşluk</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <LoadingSpinner fullPage text="Matematiksel Modeller Yükleniyor" color="blue" />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100/50 border-b border-gray-100">
                  {['Gün/Tarih','Oda','Dolu','Boş','Bloke','Yet.(+12)','Çoc.(7-12)','Beb.(0-6)','GÜNLÜK','Giriş Sayısı','Doluluk %'].map(h => (
                    <th key={h} className="py-5 px-3 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center border-r border-gray-100/50 last:border-r-0">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <EmptyState as="td" colSpan={11} icon="📊" text="İstatistiksel veri bulunamadı" />
                ) : data.map((r, idx) => {
                  const isToday = r.tarih === dateToStr(new Date());
                  return (
                    <tr key={r.tarih}
                      className={'border-b border-gray-50/50 transition-all transform-gpu hover:bg-blue-50/30 ' +
                        (isToday ? 'bg-blue-50/80 font-bold' : idx%2===0 ? 'bg-white' : 'bg-gray-50/30')}>
                      <td className="py-4 px-3 text-[11px] font-black text-gray-800 text-center whitespace-nowrap border-r border-gray-50/50">
                        {isToday && <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2 align-middle shadow-sm animate-pulse" />}
                        {r.tarih}
                      </td>
                      <td className="py-4 px-3 text-[11px] font-bold text-center border-r border-gray-50/50 text-gray-500">{r.toplamOda}</td>
                      <td className="py-4 px-3 text-center border-r border-gray-50/50">
                        <span className={'text-xs font-black ' + (r.doluOda > 0 ? 'text-blue-600' : 'text-gray-300')}>{r.doluOda}</span>
                      </td>
                      <td className="py-4 px-3 text-center border-r border-gray-50/50">
                        <span className={'text-xs font-black ' + (r.bosOda < 5 ? 'text-red-500' : 'text-green-600')}>{r.bosOda}</span>
                      </td>
                      <td className="py-4 px-3 text-center border-r border-gray-50/50">
                        <span className={'text-xs font-black ' + (r.blokeOda > 0 ? 'text-orange-500' : 'text-gray-400')}>{r.blokeOda}</span>
                      </td>
                      <td className="py-4 px-3 text-[11px] font-bold text-center border-r border-gray-50/50 text-purple-600">{r.yetiskin}</td>
                      <td className="py-4 px-3 text-[11px] font-bold text-center border-r border-gray-50/50 text-pink-600">{r.cocuk}</td>
                      <td className="py-4 px-3 text-[11px] font-bold text-center border-r border-gray-50/50 text-amber-500">{r.bebek}</td>
                      <td className="py-4 px-3 text-[11px] font-black text-center border-r border-gray-50/50 text-indigo-700 bg-indigo-50/20">{r.gunlukToplamMisafir}</td>
                      <td className="py-4 px-3 text-[11px] font-bold text-center border-r border-gray-50/50 text-slate-500">{r.girisSayisi}</td>
                      <td className="py-4 px-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                            <div className={`h-full rounded-full transition-all duration-700 shadow-sm ${r.doluluk>=80?'bg-red-500':r.doluluk>=50?'bg-yellow-400':'bg-green-500'}`}
                              style={{ width: r.doluluk + '%' }} />
                          </div>
                          <span className={`text-[10px] font-black ${r.doluluk>=80?'text-red-600':r.doluluk>=50?'text-yellow-600':'text-green-600'}`}>{r.doluluk}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {data.length > 0 && (
                  <tr className="bg-indigo-600 text-white font-black border-t-4 border-indigo-200">
                    <td className="py-6 px-3 text-[10px] uppercase tracking-widest text-center border-r border-white/10 opacity-80">TOPLAM METRİK</td>
                    <td className="py-6 px-3 text-center border-r border-white/10">—</td>
                    <td className="py-6 px-3 text-center border-r border-white/10 text-base">{toplamDolu}</td>
                    <td className="py-6 px-3 text-center border-r border-white/10 text-base">{toplamBos}</td>
                    <td className="py-6 px-3 text-center border-r border-white/10 text-base">{toplamBloke}</td>
                    <td className="py-6 px-3 text-center border-r border-white/10 text-base">{toplamYet}</td>
                    <td className="py-6 px-3 text-center border-r border-white/10 text-base">{toplamCoc}</td>
                    <td className="py-6 px-3 text-center border-r border-white/10 text-base">{toplamBeb}</td>
                    <td className="py-6 px-3 text-center border-r border-white/10 text-base bg-indigo-700">{toplamGunluk}</td>
                    <td className="py-6 px-3 text-center border-r border-white/10 text-base">{toplamGiris}</td>
                    <td className="py-6 px-3 text-center text-base">{ortDoluluk}%</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <UsageInfo
        color="blue"
        description="Forecast (Öngörü) modülü, geçmiş verilerle harmanlanmış mevcut rezervasyon hareketlerine göre otelin önümüzdeki günlerde nasıl bir misafir yapısına ve doluluk oranına sahip olacağını tahmin eden ana vizyon tablosudur. Yapay zeka destekli planlama için temel veri setidir."
        scenarios={[
          { title: "Personel Sayısını Optimize Edin", content: "Doluluğun ve misafir popülasyonunun artacağı tarihlerde (örneğin bebek sayısının artışı animatör ve housekeeping için sinyaldir) vardiyayı doğru ayarlayabilirsiniz. Gereksiz mesai maliyetlerini bu tablo ile %20'ye kadar düşürebilirsiniz." },
          { title: "Fiyatlandırma Stratejisi (Yield)", content: "Grafiklerde %80 doluluğa erken aşılan gelecekteki haftalar tespit edildiğinde Dinamik Fiyat paneline geçerek güncel fiyatlara zam kararı verebilirsiniz. Talep yüksekse düşük fiyattan oda satmayın." },
          { title: "Yıllık Bütçe ve Ciro Görüşü", content: "Ay başında bu raporu alıp, hangi hafta boşluk riski olduğunu önceden görerek OTA kanallarında (Booking, Expedia) spesifik kampanyalar (flaş indirim) başlatabilirsiniz. Doluluktaki 'dip' noktaları proaktif aksiyonla doldurun." }
        ]}
      />

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
      `}</style>
    </div>
  );
}
